<template lang="pug">
#send-message-template-list(v-cloak)
  .box
    .box-header.register-header.d-flex.flex-row
      .d-flex.flex-column
        .title
          span メッセージ送信のテンプレート
        .note
          span メッセージ送信で使用するテンプレート（タイトル、本文、オプション）を登録できます。
          br
          span 登録したテンプレートは、メッセージ送信時に選択することが出来ます。
      .flex-fill.d-flex.flex-row-reverse
        button.btn.back-btn(@click="gotoSendMessage" type="button") 戻る
        button.btn.add-btn(@click="gotoSendMessageTemplateRegister") 追加

    .box-body
      SendMessageTemplateTable(
        v-model="items"
        :fields="fields"
        :totalRows.sync="rows"
        :currentPage="currentPage"
        :perPage="perPage"
        :isLoading="isLoading"
        @onDetail="showTemplateDetailModal"
        @onEdit="gotoSendMessageTemplateEdit"
        @onDelete="showConfirmDeleteModal"
      )
      .table-options.d-flex.flex-row.justify-content-end.align-items-center
        // ページあたりの行数
        span.table-option-text 1ページの行数:
        .per-page
          b-form-select(
            id="per-page-select"
            v-model="perPage"
            :options="pageOptions"
            size="md"
            @change="selectPerPage"
          )
        // ページネーション
        b-pagination.m-0(
          v-model="currentPage"
          :total-rows="rows"
          :per-page="perPage"
          :disabled="isLoading"
          @change="selectPage"
          hide-goto-end-buttons
        )

  SendMessageTemplateDetailModal(v-model="showDetailModal" :template="detailTargetTemplate")

  OkCancelModal(v-model="showDeleteModal" :title="deleteMessage" @ok="deleteSendMessageTemplate")
  OkModal(v-model="showDeleteSuccessModal" title="削除に成功しました。" @ok="createView")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Watch, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { needRefreshesStore } from "@/store/store";
import { SendMessagesState } from "@/store/NeedRefreshes";
import { errorHandle } from "@/errorHandler";

import Setting from "../../../../public/setting.json";
import { PcUrl } from "@/commons/constants/PcUrl";

import SendMessageTemplatesService, { SendMessageTemplatesGetModel, TemplateType } from "@/commons/services/SendMessageTemplates";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import SendMessageTemplateTable, { SendMessageTemplateTableField, SendMessageTemplateTableItem } from "@/pc/containers/sendMessages/SendMessageTemplateTable.vue";
import SendMessageTemplateDetailModal from "@/pc/containers/sendMessages/SendMessageTemplateDetailModal.vue";

/**
 * ### メッセージ送信テンプレート一覧画面コンポーネント
 */
@Component({
  components: {
    PageLoading,
    OkCancelModal,
    OkModal,
    SendMessageTemplateTable,
    SendMessageTemplateDetailModal
  }
})
export default class SendMessageTemplateList extends Vue {
  /** メッセージ送信テンプレートサービス */
  @Inject("SendMessageTemplatesService") sendMessageTemplatesService!: SendMessageTemplatesService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** 詳細モーダル表示可否 */
  showDetailModal: boolean = false;
  /** 詳細モーダル表示内容 */
  detailTargetTemplate: SendMessageTemplateTableItem | null = null;

  /** 削除確認モーダル表示可否 */
  showDeleteModal: boolean = false;
  /** 削除完了モーダル表示可否 */
  showDeleteSuccessModal: boolean = false;
  /** 削除対象のメッセージ送信テンプレートID */
  deleteTargetId: number = 0;
  /** 削除モーダルに表示するメッセージ */
  deleteMessage: string = "";

  /** メッセージ送信テンプレート列名 */
  fields: Array<SendMessageTemplateTableField> = [];
  /** メッセージ送信テンプレート各行の情報 */
  items: Array<SendMessageTemplateTableItem> = [];
  /** 読込み中か否か */
  isLoading: boolean = true;
  /** ページングの現在ページ番号 */
  currentPage: number = 1;
  /** 現在取得している件数 */
  currentRows: number = 0;
  /** ページングの全体行数 */
  rows: number = 0;
  /** 1ページに表示する行数 */
  perPage: number = Number(Setting.sendMessageTemplate.perPage);
  /** 1ページに表示する行数の選択項目 */
  pageOptions: number[] = Setting.sendMessageTemplate.pageOptions;
  /** 先行して取得するページ範囲 */
  pageRange: number = Setting.sendMessageTemplate.pageRange;

  /**
   * ### 1ページあたりの表示行数の変更イベント
   *
   * - 変更をクッキーに持たせる
   */
  @Watch("perPage")
  public changePerPage(perPage: number): void {
    this.setCookie(perPage);

    return;
  }

  /**
   * ### 1ページあたりの表示行数をCookieに設定する
   */
  public setCookie(perPage: number): void {
    // Cookie有効秒数。有効期限は必ず設定しないといけないので、10年分にしておく
    const saveSecond = 10 * 365 * 24 * 60 * 60;
    document.cookie = `send-message-template-list-per-page=${perPage}; max-age=${saveSecond};`;
    return;
  }

  /**
   * ### vue lifecycle mounted
   *
   * - 画面表示情報の作成を行う
   */
  public async mounted(): Promise<void> {
    // 画面初期表示時に作成して変更しないものを初期化する
    const fields: Array<SendMessageTemplateTableField> = this.createFields();
    this.fields = fields;

    // 1ページあたりの表示行数はCookieから取得する。取得できない場合はデフォルト値を使用する
    const perPage: number | null = this.loadCookie();
    this.perPage = perPage ? perPage : Number(Setting.sendMessageTemplate.perPage);

    // 動的に変更する画面情報を作成する
    this.items = [];
    this.currentRows = this.items.length;

    await this.createView();

    return;
  }

  /**
   * ### メッセージ送信テンプレートの列名を作成する
   */
  public createFields(): Array<SendMessageTemplateTableField> {
    const fields: Array<SendMessageTemplateTableField> = [];
    fields.push({ key: "name", label: "テンプレート名", sortable: false, sortDirection: "desc", class: "name" });
    fields.push({ key: "title", label: "タイトル", sortable: false, sortDirection: "desc", class: "title" });
    fields.push({ key: "writer", label: "記載者", sortable: false, sortDirection: "desc", class: "writer" });
    fields.push({ key: "operation", label: "操作", sortable: false, sortDirection: "desc", class: "operation text-center" });
    return fields;
  }

  /**
   * ### Cookieを見て、値が保持されている場合はCookieの値を返す
   */
  public loadCookie(): number | null {
    if (document.cookie) {
      let cookieMap: any = {};
      let cookies: Array<string> = document.cookie.split("; ");
      cookies.forEach(function (value) {
        //cookie名と値に分ける
        let content = value.split("=");
        cookieMap[content[0]] = content[1];
      });

      // cookieに値が格納されていないとundefinedが格納されているが、実際にはゴミデータが格納されているのでisNaN()でチェックが必要
      const perPage: number = cookieMap["send-message-template-list-per-page"];

      return isNaN(perPage) ? null : perPage;
    }

    return null;
  }

  /**
   * ### 画面情報作成
   */
  public async createView(): Promise<void> {
    this.isLoading = true;

    // 全体の行数を非同期で取得する
    this.setRows();

    // メッセージ送信テンプレートテーブルに渡す各プロパティを作成する
    const tempaltes: Array<SendMessageTemplatesGetModel> = await this.getSendMessageTemplates();
    const filterdTemplates: Array<SendMessageTemplatesGetModel> = this.filteringByTemplateType(tempaltes);
    const items: Array<SendMessageTemplateTableItem> = this.createItems(filterdTemplates);
    this.items = items;
    this.currentRows = this.items.length;

    this.isLoading = false;

    return;
  }

  /**
   * ### ページング用の全件数を設定する
   */
  public async setRows() {
    try {
      const tempaltes: Array<SendMessageTemplatesGetModel> = await this.getSendMessageTemplates();
      const filterdTemplates: Array<SendMessageTemplatesGetModel> = this.filteringByTemplateType(tempaltes);
      this.rows = filterdTemplates.length;
    } catch (error) {
      // 本処理はawaitされない非同期処理なので、共通エラーハンドラーが例外をキャッチしない
      // 例外発生時は自分でエラーハンドラーを呼ぶ
      errorHandle(error, this);
    }
    return;
  }

  /**
   * ### メッセージ送信テンプレートを取得する
   */
  public async getSendMessageTemplates(): Promise<Array<SendMessageTemplatesGetModel>> {
    const records: Array<SendMessageTemplatesGetModel> = await this.sendMessageTemplatesService.gets(userStore);
    return records;
  }

  /**
   * ### メッセージ送信テンプレートをテンプレート種別でフィルタリングする
   *
   * - テンプレート種別が「待機者」のもののみ返却する
   */
  public filteringByTemplateType(templates: Array<SendMessageTemplatesGetModel>): Array<SendMessageTemplatesGetModel> {
    return templates.concat().filter((temaplte) => {
      return temaplte.templateType == TemplateType.MESSAGE ? true : false;
    });
  }

  /**
   * ### メッセージ送信テンプレートの行を作成する
   */
  public createItems(sendMessageTemplates: Array<SendMessageTemplatesGetModel>): Array<SendMessageTemplateTableItem> {
    return sendMessageTemplates;
  }

  /**
   * ### 1ページあたりの表示行数変更イベント
   *
   * - 1ページあたりの表示行数を更新
   * - ページあたりの表示行数が変わるので表の情報を作り直す
   */
  public async selectPerPage(perPage: number): Promise<void> {
    this.perPage = perPage;
    this.currentPage = 1;
    return;
  }

  /**
   * ### ページネーションのページ番号押下
   */
  public async selectPage(page: number): Promise<void> {
    this.currentPage = page;
    return;
  }

  /**
   * ### メッセージ送信画面へ遷移
   */
  public gotoSendMessage(): void {
    this.$router.push(PcUrl.SendMessage);
    return;
  }

  /**
   * ### メッセージ送信テンプレート登録画面へ遷移
   */
  public gotoSendMessageTemplateRegister(): void {
    this.$router.push(PcUrl.SendMessageTemplateRegister);
    return;
  }

  /**
   * ### 詳細モーダルを表示する
   */
  public showTemplateDetailModal(template: SendMessageTemplateTableItem): void {
    this.detailTargetTemplate = template;
    this.showDetailModal = true;
    return;
  }

  /**
   * ### メッセージ送信テンプレート編集画面へ遷移
   */
  public gotoSendMessageTemplateEdit(template: SendMessageTemplateTableItem): void {
    this.$router.push({ name: PcUrl.SendMessageTemplateEdit, params: { id: `${template.id}` } });
    return;
  }

  /**
   * ### 削除確認モーダルを表示する
   *
   * - 削除メッセージは動的に作成する
   */
  public showConfirmDeleteModal(template: SendMessageTemplateTableItem): void {
    const name: string = template.name ? template.name : "";
    this.deleteMessage = `${name}を削除しますか？`;
    this.showDeleteModal = true;

    this.deleteTargetId = template.id;

    return;
  }

  /**
   * ### メッセージ送信テンプレートを削除する
   */
  public async deleteSendMessageTemplate(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "削除中...";

    await this.sendMessageTemplatesService.delete(userStore, this.deleteTargetId);

    // メッセージ送信のテンプレートが変更になるので再読み込みさせるようstoreを更新
    let sendMessageState: SendMessagesState = needRefreshesStore.sendMessage;
    sendMessageState.sendMessages = true;
    needRefreshesStore.setSendMessage(sendMessageState);

    // 削除成功ダイアログを表示する
    this.showDeleteSuccessModal = true;
    this.isBusy = false;

    // 削除による画面の再描画もあるので、現在ページ番号はここで初期化する
    this.currentPage = 1;
    await this.createView();

    return;
  }
}
</script>

<style lang="scss" scoped>
// 親要素からネストしてスタイルを指定する
#send-message-template-list {
  max-width: 1280px;
  margin-left: auto;
  margin-right: auto;

  .add-btn,
  .back-btn {
    width: 80px;
  }
  .add-btn {
    margin-right: 12px;
  }
}
</style>
